import { useCallback } from 'react';
import { useQuery, useQueryClient, type UseQueryOptions } from '@tanstack/react-query';
import { fetchDxReportDrafts } from '../services/clinicalDrafts';
import { DX_REPORT_DRAFTS_KEY } from './queryKeys';
import type { TimelineEvent } from '../components/patient-files/actions/types';

export function useDxReportDrafts(
    patientId: string | undefined,
    options: Pick<UseQueryOptions<TimelineEvent[]>, 'refetchOnMount'> = {}
) {
    const { data, isLoading, error } = useQuery<TimelineEvent[]>({
        queryKey: DX_REPORT_DRAFTS_KEY(patientId ?? ''),
        queryFn: () => fetchDxReportDrafts(patientId!),
        enabled: !!patientId,
        staleTime: 5 * 60 * 1000,
        ...options
    });

    return { drafts: data ?? [], isLoading, error };
}

// Every draft write calls this so gates reading the drafts (pre-approve, Plan received,
// Final review) react without leaving the editor.
export function useInvalidateDxReportDrafts(patientId: string): () => void {
    const queryClient = useQueryClient();
    return useCallback(() => {
        void queryClient.invalidateQueries({ queryKey: DX_REPORT_DRAFTS_KEY(patientId) });
    }, [queryClient, patientId]);
}
